import { useState, useMemo } from 'react'
import { toast } from 'react-hot-toast'
import {
    RiImageLine,
    RiFileChartLine,
    RiFilePdfLine,
    RiDownloadLine,
    RiCheckLine,
    RiCheckboxMultipleLine,
    RiCheckboxBlankLine,
} from 'react-icons/ri'
import { VscFileBinary } from 'react-icons/vsc'
import { exportDesign } from '@/lib/exportCanvas'
import type { ExportFormat, ExportQuality } from '@/lib/exportCanvas'
import { useProjectStore } from '@/stores/useProjectStore'
import { useEditorStore } from '@/stores/useEditorStore'
import { useModalStore } from '@/stores/useModalStore'
import styles from './ExportModal.module.css'

interface FormatOption {
    value: ExportFormat
    icon: React.ComponentType<{ className?: string; size?: number }>
    name: string
    desc: string
    ext: string
}

const FORMAT_OPTIONS: FormatOption[] = [
    {
        value: 'png',
        icon: RiImageLine,
        name: 'PNG',
        desc: 'Alta calidad con transparencia',
        ext: '.png',
    },
    {
        value: 'jpg',
        icon: RiFileChartLine,
        name: 'JPG',
        desc: 'Compacto, ideal para compartir',
        ext: '.jpg',
    },
    {
        value: 'pdf',
        icon: RiFilePdfLine,
        name: 'PDF estándar',
        desc: 'Documento listo para imprimir',
        ext: '.pdf',
    },
    {
        value: 'svg',
        icon: VscFileBinary,
        name: 'SVG',
        desc: 'Vectorial, escalable sin pérdida',
        ext: '.svg',
    },
]

const QUALITY_OPTIONS: { value: ExportQuality; label: string; detail: string }[] = [
    { value: 1, label: 'Estándar', detail: '96 PPP' },
    { value: 2, label: 'Alta', detail: '192 PPP' },
    { value: 4, label: 'Ultra', detail: '384 PPP' },
]

interface ExportModalProps {
    handleClose?: () => void
}

export function ExportModal(_props: ExportModalProps) {
    const { projectName, widthMm, heightMm, pages } = useProjectStore()
    const currentPageIndex = useEditorStore((s) => s.currentPageIndex)
    const { closeModal } = useModalStore()

    const [format, setFormat] = useState<ExportFormat>('png')
    const [quality, setQuality] = useState<ExportQuality>(2)
    const [selectedPages, setSelectedPages] = useState<Set<number>>(() => new Set([currentPageIndex]))
    const [cropMarks, setCropMarks] = useState(false)
    const [bleed, setBleed] = useState(false)
    const [isExporting, setIsExporting] = useState(false)

    const allSelected = selectedPages.size === pages.length
    const noneSelected = selectedPages.size === 0

    const togglePage = (index: number) => {
        setSelectedPages((prev) => {
            const next = new Set(prev)
            if (next.has(index)) {
                next.delete(index)
            } else {
                next.add(index)
            }
            return next
        })
    }

    const toggleAll = () => {
        if (allSelected) {
            setSelectedPages(new Set([currentPageIndex]))
        } else {
            setSelectedPages(new Set(pages.map((_, i) => i)))
        }
    }

    const handleExport = async () => {
        if (noneSelected) {
            toast.error('Selecciona al menos una página')
            return
        }
        setIsExporting(true)
        try {
            await exportDesign({
                format,
                quality,
                selectedPages: Array.from(selectedPages).sort((a, b) => a - b),
                cropMarks,
                bleed,
                projectName: projectName || 'diseno',
                widthMm,
                heightMm,
            })
            const pageCount = selectedPages.size
            const multiSuffix = pageCount > 1 && format !== 'pdf' ? ' (ZIP)' : ''
            toast.success(`Exportado como ${format.toUpperCase()}${multiSuffix}`)
            closeModal()
        } catch {
            toast.error('Error al exportar el diseño')
        } finally {
            setIsExporting(false)
        }
    }

    const isPdf = format === 'pdf'
    const isSvg = format === 'svg'
    const showQuality = !isSvg
    const showPrintOptions = isPdf
    const multiPageNote = useMemo(() => {
        if (selectedPages.size <= 1) return null
        if (isPdf) return 'Se generará un PDF con todas las páginas seleccionadas'
        return 'Se descargará un archivo ZIP con las páginas seleccionadas'
    }, [selectedPages.size, isPdf])

    return (
        <div className={styles.modal}>
            <div className={styles.header}>
                <h2 className={styles.title}>Exportar diseño</h2>
                <p className={styles.subtitle}>
                    {widthMm} x {heightMm} mm
                </p>
            </div>

            <div className={styles.body}>
                {/* Left column: Format + Options */}
                <div className={styles.optionsColumn}>
                    <div className={styles.section}>
                        <span className={styles.sectionLabel}>Tipo de archivo</span>
                        <div className={styles.formatGrid}>
                            {FORMAT_OPTIONS.map((opt) => {
                                const Icon = opt.icon
                                const isActive = format === opt.value
                                return (
                                    <button
                                        key={opt.value}
                                        className={`${styles.formatCard} ${isActive ? styles.formatCardActive : ''}`}
                                        onClick={() => setFormat(opt.value)}
                                        type="button"
                                    >
                                        <Icon className={styles.formatIcon} />
                                        <div className={styles.formatInfo}>
                                            <span className={styles.formatName}>{opt.name}</span>
                                            <span className={styles.formatDesc}>{opt.desc}</span>
                                        </div>
                                        {isActive && (
                                            <RiCheckLine className={styles.formatCheck} />
                                        )}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    {showQuality && (
                        <div className={styles.section}>
                            <span className={styles.sectionLabel}>Calidad</span>
                            <div className={styles.qualityRow}>
                                {QUALITY_OPTIONS.map((opt) => (
                                    <button
                                        key={opt.value}
                                        className={`${styles.qualityChip} ${quality === opt.value ? styles.qualityChipActive : ''}`}
                                        onClick={() => setQuality(opt.value)}
                                        type="button"
                                    >
                                        <span className={styles.qualityLabel}>{opt.label}</span>
                                        <span className={styles.qualityDetail}>{opt.detail}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {showPrintOptions && (
                        <div className={styles.section}>
                            <span className={styles.sectionLabel}>Opciones de impresión</span>
                            <label className={styles.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={cropMarks}
                                    onChange={(e) => setCropMarks(e.target.checked)}
                                />
                                <span>Marcas de corte</span>
                            </label>
                            <label className={styles.checkbox}>
                                <input
                                    type="checkbox"
                                    checked={bleed}
                                    onChange={(e) => setBleed(e.target.checked)}
                                />
                                <span>Sangrado (3 mm)</span>
                            </label>
                        </div>
                    )}
                </div>

                {/* Right column: Page selection */}
                {pages.length > 1 && (
                    <div className={styles.pagesColumn}>
                        <div className={styles.pagesHeader}>
                            <span className={styles.sectionLabel}>Páginas</span>
                            <button
                                className={styles.selectAllBtn}
                                onClick={toggleAll}
                                type="button"
                            >
                                {allSelected ? (
                                    <>
                                        <RiCheckboxMultipleLine />
                                        Deseleccionar
                                    </>
                                ) : (
                                    <>
                                        <RiCheckboxBlankLine />
                                        Todas
                                    </>
                                )}
                            </button>
                        </div>
                        <div className={styles.pageGrid}>
                            {pages.map((page, index) => {
                                const isSelected = selectedPages.has(index)
                                return (
                                    <button
                                        key={page.id}
                                        className={`${styles.pageThumb} ${isSelected ? styles.pageThumbSelected : ''}`}
                                        onClick={() => togglePage(index)}
                                        type="button"
                                        title={`Página ${index + 1}`}
                                    >
                                        <div className={styles.pagePreview}>
                                            {page.thumbnail_url ? (
                                                <img
                                                    src={page.thumbnail_url}
                                                    alt={`Página ${index + 1}`}
                                                    className={styles.pageImg}
                                                />
                                            ) : (
                                                <div className={styles.pagePlaceholder}>
                                                    {index + 1}
                                                </div>
                                            )}
                                            {isSelected && (
                                                <div className={styles.pageCheckBadge}>
                                                    <RiCheckLine />
                                                </div>
                                            )}
                                        </div>
                                        <span className={styles.pageLabel}>
                                            Pág. {index + 1}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className={styles.footer}>
                {multiPageNote && (
                    <p className={styles.footerNote}>{multiPageNote}</p>
                )}
                <div className={styles.footerActions}>
                    <button
                        className={styles.cancelBtn}
                        onClick={closeModal}
                        type="button"
                        disabled={isExporting}
                    >
                        Cancelar
                    </button>
                    <button
                        className={styles.exportBtn}
                        onClick={handleExport}
                        disabled={isExporting || noneSelected}
                        type="button"
                    >
                        {isExporting ? (
                            <span className={styles.exportingText}>Exportando...</span>
                        ) : (
                            <>
                                <RiDownloadLine />
                                <span>
                                    Descargar {format.toUpperCase()}
                                    {selectedPages.size > 1 ? ` (${selectedPages.size})` : ''}
                                </span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}
